import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  LoadEvent,
  LoadEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/trade/load_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { LoadCreatedDetail } from './LoadCreatedDetail';
import { LoadDeletedDetail } from './LoadDeletedDetail';
import { LoadStatusChangedDetail } from './LoadStatusChangedDetail';
import { LoadUpdatedDetail } from './LoadUpdatedDetail';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type LoadEventDetailProps = {
  payload: string;
};

export const LoadEventDetail = ({ payload }: LoadEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): LoadEvent | null => {
    try {
      return fromJson(LoadEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return (
      <JsonViewer data={payload} title={t.eventLogs.rawPayloadParseError} />
    );
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <LoadCreatedDetail event={inner.value} />;
      case 'updated':
        return <LoadUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <LoadDeletedDetail event={inner.value} />;
      case 'statusChanged':
        return <LoadStatusChangedDetail event={inner.value} />;
      case undefined:
        return (
          <JsonViewer data={payload} title={t.eventLogs.unknownEventType} />
        );
    }
  };

  const getEventTitle = (): string => {
    switch (inner.case) {
      case 'created':
        return t.eventLogs.load.events.created;
      case 'updated':
        return t.eventLogs.load.events.updated;
      case 'deleted':
        return t.eventLogs.load.events.deleted;
      case 'statusChanged':
        return t.eventLogs.load.events.statusChanged;
      default:
        return t.eventLogs.unknownEvent;
    }
  };

  return (
    <Col className="@LoadEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
